import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import { Icon } from 'components/Icon/Icon';
import { useToast } from 'components/Toast/use-toast';
import { UserSuspendModal } from 'components/UserSuspendModal/UserSuspendModal';
import { UserTermiateModal } from 'components/UserTerminateModal/UserTerminateModal';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import {
    BLOCK_MEMBER,
    MEMBER_PUBLISH_UNPUBLISH,
    MEMBER_SUSPEND,
    MEMBER_TERMINATE,
    MEMBER_UNSUSPEND,
    RESET_PASSWORD,
    UPDATE_MEMBER,
    USER_DELETE,
} from 'services/users/mutation';

import { BlockMemberModal } from '../BlockMemberModal/BlockMemberModal';
import { UnblockMemberModal } from '../UnblockMemberModal/UnblockMemberModal';
import { UserDeleteModal } from '../UserDeleteModal/UserDeleteModal';

import styles from './UserSettings.module.scss';

type UserSettingsProps = {
    user: any;
    actions: string[];
    onChange?: () => void;
    navigateTo?: string;
};

export const UserSettings: React.FC<UserSettingsProps> = ({ user, actions, onChange, navigateTo }) => {
    const menu = useRef<any>(null);
    const [isActive, setIsActive] = useState(false);
    const [blockModal, setBlockModal] = useState(false);
    const [unblockModal, setUnblockModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [suspendModal, setSuspendModal] = useState(false);
    const [terminateModal, setTerminateModal] = useState(false);
    const { t } = useTranslation('member', { keyPrefix: 'userSettings' });
    const { toast } = useToast();
    const navigate = useNavigate();

    const isMemberBlocked = user?.status === 'BLOCKED';
    // Scheduled suspensions keep the member ACTIVE but already carry the
    // dates; both running and scheduled suspensions are lifted via unsuspend.
    const isMemberSuspended = user?.status === 'SUSPENDED' || !!user?.suspendedFrom;
    const isMemberTerminatedOrInactive = user?.status === 'TERMINATED' || user?.status === 'INACTIVE';

    const [blockUnblock] = useMutation(BLOCK_MEMBER);
    const [resetPassword] = useMutation(RESET_PASSWORD);
    const [deleteUser] = useMutation(USER_DELETE);
    const [suspendUser] = useMutation(MEMBER_SUSPEND);
    const [unsuspendUser] = useMutation(MEMBER_UNSUSPEND);
    const [terminateUser] = useMutation(MEMBER_TERMINATE);
    const [updateMember] = useMutation(UPDATE_MEMBER);
    const [publishMember] = useMutation(MEMBER_PUBLISH_UNPUBLISH);

    const handleClickOutside = (event: any) => {
        if (menu.current && !menu.current.contains(event.target)) {
            setIsActive(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    // PASWORD RESET LOGIC
    const handlePasswordReset = () => {
        const userEmail = user?.email;
        resetPassword({ variables: { email: userEmail } }).then(r => {
            if (r.data.resetPassword.success) {
                toast({
                    title: t('passwordResetSuccessTitle'),
                    description: t('passwordResetSuccessText').replace('{email}', user ? user?.email : ''),
                    variant: 'success',
                });
            } else {
                failedToast();
            }
        });
    };

    // PERMANENTLY DELETE LOGIC
    const handleDeleteUser = () => {
        const userId = user?.id;
        deleteUser({ variables: { user: userId } }).then(r => {
            if (r.data.userDelete.success) {
                setDeleteModal(false);
                toast({
                    title: t('deleteSuccess'),
                    variant: 'success',
                });
                navigate(navigateTo || AppRoutes.members, { replace: true });
            } else {
                failedToast();
            }
        });
    };

    // BLOCK UNBLOCK LOGIC
    const handleBlockUnblockMember = () => {
        isMemberBlocked ? setUnblockModal(true) : setBlockModal(true);
    };

    const blockMember = () => {
        const userId = user?.id;
        blockUnblock({ variables: { user: userId, action: 'BLOCK' } }).then(r => {
            if (r.data.userBlock) {
                toast({
                    description: t('blockSuccess'),
                    variant: 'success',
                });
            } else {
                failedToast();
            }
            if (onChange) onChange();

            setBlockModal(false);
        });
    };

    const unblockMember = () => {
        const userId = user?.id;
        blockUnblock({ variables: { user: userId, action: 'UNBLOCK' } }).then(r => {
            if (r.data.userBlock) {
                toast({
                    description: t('unblockSuccess'),
                    variant: 'success',
                });
            } else {
                failedToast();
            }
            if (onChange) onChange();
            setUnblockModal(false);
        });
    };

    const handleSuspend = (data: any) => {
        const userId = user?.id;
        suspendUser({ variables: { member: userId, ...data } }).then(r => {
            if (r.data.memberSuspend?.status) {
                toast({
                    title: t('suspendSuccess'),
                    variant: 'success',
                });
                setSuspendModal(false);
                if (onChange) onChange();
            } else {
                failedToast();
            }
        });
    };

    const handleUnsuspend = () => {
        const userId = user?.id;
        unsuspendUser({ variables: { member: userId } }).then(r => {
            if (r.data.memberUnsuspend?.status) {
                toast({
                    title: t('unsuspendSuccess'),
                    variant: 'success',
                });
                if (onChange) onChange();
            } else {
                failedToast();
            }
        });
    };

    const handleTerminateUser = () => {
        const userId = user?.id;
        terminateUser({ variables: { member: userId } })
            .then(r => {
                if (r.data?.memberTerminate?.user?.id) {
                    toast({
                        title: t('terminateSuccess'),
                        variant: 'success',
                    });
                    if (onChange) onChange();
                } else {
                    const errorMessage = r.errors?.[0]?.message;
                    toast({
                        title: t('failToastTitle'),
                        description: errorMessage || t('failToastText'),
                        variant: 'warning',
                    });
                }
                setTerminateModal(false);
            })
            .catch(() => {
                failedToast();
                setTerminateModal(false);
            });
    };

    const handleToggleDoNotRenew = () => {
        const userId = user?.id;
        const newDoNotRenewValue = !user?.doNotRenew;
        updateMember({
            variables: {
                user: userId,
                input: {
                    doNotRenew: newDoNotRenewValue,
                },
            },
        }).then(r => {
            if (r.data.memberUpdate?.user?.id) {
                toast({
                    title: newDoNotRenewValue ? t('doNotRenewEnabled') : t('doNotRenewDisabled'),
                    variant: 'success',
                });
                if (onChange) onChange();
            } else {
                failedToast();
            }
        });
    };

    const handleToggleHideFromMemberList = () => {
        const userId = user?.id;
        const newHideFromMemberListValue = !user?.hideFromMemberList;
        updateMember({
            variables: {
                user: userId,
                input: {
                    hideFromMemberList: newHideFromMemberListValue,
                },
            },
        }).then(r => {
            if (r.data.memberUpdate?.user?.id) {
                toast({
                    title: newHideFromMemberListValue ? t('hideFromMemberListEnabled') : t('hideFromMemberListDisabled'),
                    variant: 'success',
                });
                if (onChange) onChange();
            } else {
                failedToast();
            }
        });
    };

    const handleReactivate = () => {
        const userId = user?.id;
        publishMember({ variables: { user: userId, action: 'PUBLISH' } }).then(r => {
            if (r.errors && r.errors?.length > 0) {
                failedToast();
                return;
            }
            if (r.data?.userPublish?.status) {
                toast({
                    title: t('reactivateSuccess'),
                    variant: 'success',
                });
                if (onChange) onChange();
            } else {
                failedToast();
            }
        });
    };

    const failedToast = () => {
        toast({
            title: t('failToastTitle'),
            description: t('failToastText'),
            variant: 'warning',
        });
    };

    return (
        <div
            className={clsx(styles.userSettings, isActive && styles.active)}
            onClick={() => setIsActive(!isActive)}
            ref={menu}
        >
            <div className={styles.trigger}>
                {t('settings')} <Icon name="carret-down" />
            </div>

            <div className={styles.dropdown}>
                <div className={styles.dropdownContent}>
                    {actions.includes('BLOCK') && (
                        <div className={styles.dropdownItem} onClick={() => handleBlockUnblockMember()}>
                            <Icon name="block" /> <span>{isMemberBlocked ? t('unblockMember') : t('blockMember')}</span>
                        </div>
                    )}
                    {actions.includes('PASSWORD_RESET') && (
                        <div className={styles.dropdownItem} onClick={() => handlePasswordReset()}>
                            <Icon name="lock" /> <span>{t('passwordReset')}</span>
                        </div>
                    )}

                    {/* <div className={styles.dropdownItem}>
                        <Icon name="export" /> <span>{t('export')}</span>
                    </div> */}
                    {actions.includes('SUSPEND') && (
                        <>
                            {isMemberSuspended && (
                                <div className={styles.dropdownItem} onClick={handleUnsuspend}>
                                    <Icon name="suspend" /> <span>{t('unsuspend')}</span>
                                </div>
                            )}
                            {!isMemberSuspended && (
                                <div className={styles.dropdownItem} onClick={() => setSuspendModal(true)}>
                                    <Icon name="suspend" /> <span>{t('suspend')}</span>
                                </div>
                            )}
                        </>
                    )}
                    {actions.includes('DO_NOT_RENEW') && (
                        <div className={styles.dropdownItem} onClick={handleToggleDoNotRenew}>
                            {user?.doNotRenew ? <Icon name="user" /> : <Icon name="user-negative" />}
                            <span>{user?.doNotRenew ? t('renewAsNormal') : t('doNotRenew')}</span>
                        </div>
                    )}
                    {actions.includes('HIDE_FROM_MEMBER_LIST') && (
                        <div className={styles.dropdownItem} onClick={handleToggleHideFromMemberList}>
                            {user?.hideFromMemberList ? <Icon name="eye" /> : <Icon name="close" />}
                            <span>{user?.hideFromMemberList ? t('showOnMemberList') : t('hideFromMemberList')}</span>
                        </div>
                    )}
                    {actions.includes('TERMINATE') && (
                        <div className={styles.dropdownItem} onClick={() => setTerminateModal(true)}>
                            <Icon name="terminate" /> <span>{t('terminate')}</span>
                        </div>
                    )}
                    {actions.includes('REACTIVATE') && isMemberTerminatedOrInactive && (
                        <div className={styles.dropdownItem} onClick={handleReactivate}>
                            <Icon name="user" /> <span>{t('reactivate')}</span>
                        </div>
                    )}
                    {actions.includes('DELETE') && (
                        <div className={styles.dropdownItem} onClick={() => setDeleteModal(true)}>
                            <Icon name="trash" /> <span>{t('deletePermanently')}</span>
                        </div>
                    )}
                </div>
            </div>

            <BlockMemberModal isOpen={blockModal} onClose={() => setBlockModal(false)} onBlock={blockMember} />

            <UnblockMemberModal
                isOpen={unblockModal}
                onClose={() => setUnblockModal(false)}
                onUnblock={unblockMember}
            />

            <UserDeleteModal
                isOpen={deleteModal}
                onClose={() => setDeleteModal(false)}
                onDelete={handleDeleteUser}
                user={user}
            />
            <UserSuspendModal isOpen={suspendModal} onClose={() => setSuspendModal(false)} onSuspend={handleSuspend} />
            <UserTermiateModal
                isOpen={terminateModal}
                onClose={() => setTerminateModal(false)}
                onConfirm={handleTerminateUser}
            />
        </div>
    );
};
